/**
 * Ceremony Field Placement Step (B3)
 *
 * "Prepare Document" step in ceremony creation.
 * Sender drags signature fields onto the PDF for each participant.
 *
 * Layout: PDF with overlay (left) | Participant panel (right)
 * Each participant gets a colored "Click to place" button.
 * Continue blocked until all Signers/Approvers have ≥1 field.
 */

import { useState } from 'react';
import {
  Box,
  Group,
  Stack,
  Title,
  Text,
  Button,
  Paper,
  Badge,
  ActionIcon,
  SegmentedControl,
  Alert,
  ScrollArea,
} from '@mantine/core';
import {
  IconSignature,
  IconLetterCase,
  IconCalendar,
  IconTextSize,
  IconTrash,
  IconAlertCircle,
  IconCheck,
} from '@tabler/icons-react';
import { PDFViewer } from '@/components/pdf/PDFViewer';
import {
  SignatureFieldPlacementOverlay,
  type PlacedSignatureField,
  type SignatureFieldTypeValue,
} from '@/components/pdf/SignatureFieldPlacementOverlay';
import { SignerRole } from '@/generated/graphql';

// Participant data shape passed in from SigningCeremonyPage
export interface ParticipantDraft {
  name: string;
  email: string;
  role: string;
  fieldName: string;
}

interface CeremonyFieldPlacementStepProps {
  documentId: string;
  pdfUrl: string;
  participants: ParticipantDraft[];
  placedFields: PlacedSignatureField[];
  onFieldsChange: (fields: PlacedSignatureField[]) => void;
  onNext: () => void;
  onBack: () => void;
}

// Color names for participant badges
const PARTICIPANT_BADGE_COLORS = [
  'blue', 'green', 'orange', 'grape', 'teal', 'red', 'yellow', 'indigo',
] as const;

const FIELD_TYPE_OPTIONS: Array<{ value: SignatureFieldTypeValue; label: string; icon: React.ReactNode }> = [
  { value: 'Signature', label: 'Sign', icon: <IconSignature size={14} /> },
  { value: 'Initials', label: 'Init', icon: <IconLetterCase size={14} /> },
  { value: 'DateSigned', label: 'Date', icon: <IconCalendar size={14} /> },
  { value: 'TextField', label: 'Text', icon: <IconTextSize size={14} /> },
];

function needsField(role: string): boolean {
  return role === SignerRole.SIGNER || role === SignerRole.APPROVER;
}

export function CeremonyFieldPlacementStep({
  documentId,
  pdfUrl,
  participants,
  placedFields,
  onFieldsChange,
  onNext,
  onBack,
}: CeremonyFieldPlacementStepProps) {
  const [activePIdx, setActivePIdx] = useState<number | null>(null);
  const [activeFieldType, setActiveFieldType] = useState<SignatureFieldTypeValue>('Signature');
  const currentPage = 1;

  const participantNames = participants.map(
    (p, i) => p.name || `Participant ${i + 1}`,
  );

  // Every participant index that is a signer/approver must have at least one field
  const requiredIndices = participants
    .map((p, i) => ({ p, i }))
    .filter(({ p }) => needsField(p.role))
    .map(({ i }) => i);
  const canContinue = requiredIndices.every((idx) =>
    placedFields.some((f) => f.participantIndex === idx),
  );

  const handleFieldPlaced = (field: PlacedSignatureField) => {
    onFieldsChange([...placedFields, field]);
  };

  const handleFieldDeleted = (id: string) => {
    onFieldsChange(placedFields.filter((f) => f.id !== id));
  };

  const clearParticipantFields = (pIdx: number) => {
    onFieldsChange(placedFields.filter((f) => f.participantIndex !== pIdx));
  };

  return (
    <Group align="flex-start" gap="md" style={{ height: '100%', minHeight: 600 }}>
      {/* Left: PDF with overlay */}
      <Box style={{ flex: 1, position: 'relative', minHeight: 600 }}>
        <Box style={{ position: 'relative' }}>
          <PDFViewer
            url={pdfUrl}
            documentId={documentId}
            annotations={[]}
            onAnnotationsChange={() => {}}
            canAnnotate={false}
            canSign={false}
          />
          <Box
            style={{
              position: 'absolute',
              inset: 0,
              pointerEvents: activePIdx !== null ? 'auto' : 'none',
            }}
          >
            <SignatureFieldPlacementOverlay
              page={currentPage}
              activeParticipantIndex={activePIdx}
              activeFieldType={activeFieldType}
              fields={placedFields}
              onFieldPlaced={handleFieldPlaced}
              onFieldDeleted={handleFieldDeleted}
              mode={activePIdx !== null ? 'place' : null}
              participantNames={participantNames}
            />
          </Box>
        </Box>
        {activePIdx !== null && (
          <Alert color="blue" mt="xs" style={{ fontSize: 13 }}>
            Drag to draw a <strong>{activeFieldType}</strong> field for{' '}
            <strong>{participantNames[activePIdx]}</strong>. Click a participant again to deselect.
          </Alert>
        )}
      </Box>

      {/* Right: Participant panel */}
      <Box style={{ width: 280, flexShrink: 0 }}>
        <Stack gap="sm">
          <Title order={5}>Place Signature Fields</Title>
          <Text size="sm" c="dimmed">
            Select a participant, then drag on the PDF to place their signature field.
          </Text>

          {/* Field type selector */}
          <SegmentedControl
            size="xs"
            data={FIELD_TYPE_OPTIONS.map((ft) => ({ value: ft.value, label: ft.label }))}
            value={activeFieldType}
            onChange={(v) => setActiveFieldType(v as SignatureFieldTypeValue)}
          />

          <ScrollArea.Autosize mah={400}>
            <Stack gap="xs">
              {participants.map((p, idx) => {
                const color = PARTICIPANT_BADGE_COLORS[idx % PARTICIPANT_BADGE_COLORS.length];
                const fieldsForP = placedFields.filter((f) => f.participantIndex === idx);
                const isActive = activePIdx === idx;
                const required = needsField(p.role);

                return (
                  <Paper
                    key={idx}
                    p="xs"
                    withBorder
                    style={{
                      cursor: 'pointer',
                      borderColor: isActive ? 'var(--mantine-color-blue-5)' : undefined,
                      background: isActive ? 'var(--mantine-color-blue-0)' : undefined,
                    }}
                    onClick={() => setActivePIdx(isActive ? null : idx)}
                  >
                    <Group justify="space-between" mb={4}>
                      <Group gap="xs">
                        <Badge color={color} size="xs">
                          {p.role}
                        </Badge>
                        <Text size="sm" fw={500}>
                          {p.name || 'Unnamed'}
                        </Text>
                      </Group>
                      {required && fieldsForP.length > 0 && (
                        <IconCheck size={14} color="var(--mantine-color-green-6)" />
                      )}
                    </Group>
                    <Text size="xs" c="dimmed">
                      {p.email || 'No email'}
                    </Text>
                    {fieldsForP.length > 0 && (
                      <Group mt={4} gap={4}>
                        <Text size="xs" c="dimmed">
                          {fieldsForP.length} field{fieldsForP.length > 1 ? 's' : ''}
                        </Text>
                        <ActionIcon
                          size="xs"
                          variant="subtle"
                          color="red"
                          onClick={(e) => {
                            e.stopPropagation();
                            clearParticipantFields(idx);
                          }}
                        >
                          <IconTrash size={10} />
                        </ActionIcon>
                      </Group>
                    )}
                    {required && fieldsForP.length === 0 && (
                      <Text size="xs" c="orange" mt={2}>
                        Needs a field
                      </Text>
                    )}
                  </Paper>
                );
              })}
            </Stack>
          </ScrollArea.Autosize>

          {!canContinue && (
            <Alert icon={<IconAlertCircle size={14} />} color="orange">
              <Text size="xs">Place at least one field for each signer/approver to continue.</Text>
            </Alert>
          )}

          <Group mt="sm">
            <Button variant="default" onClick={onBack}>
              Back
            </Button>
            <Button disabled={!canContinue} onClick={onNext} style={{ flex: 1 }}>
              Continue
            </Button>
          </Group>
        </Stack>
      </Box>
    </Group>
  );
}
